// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: bd4f8ff3-7717-439e-9eee-66449a0fd468

import React, { useEffect, useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, Select, MenuItem, Chip, Stack,
  Menu, Divider, IconButton, Drawer, List, ListItemButton, ListItemText, ListSubheader, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import Inventory from './Inventory';
import Dashboard from './Dashboard';
import FactSheetDetail from './FactSheetDetail';
import MasterCatalog from './MasterCatalog';
import LandscapeReport from './LandscapeReport';
import MatrixReport from './MatrixReport';
import ReadinessView from './ReadinessView';
import InterfaceHub from './InterfaceHub';
import CostModel from './CostModel';
import SurveyWorkflow from './SurveyWorkflow';
import FileExchange from './FileExchange';
import SavedViews from './SavedViews';
import FormsRoundtrip from './FormsRoundtrip';
import SnapshotCompare from './SnapshotCompare';
import Benchmarks from './Benchmarks';
import WavePlanner from './WavePlanner';
import DocumentExtract from './DocumentExtract';
import InfrastructureImport from './InfrastructureImport';
import RelationshipWizard from './RelationshipWizard';
import CsgTargetState from './CsgTargetState';
import ServerMapping from './ServerMapping';
import Rightsizing from './Rightsizing';
import TemplateLibrary from './TemplateLibrary';
import RelationMap from './RelationMap';
import CatalogIntelligence from './CatalogIntelligence';
import PortfolioReport from './PortfolioReport';
import Obsolescence from './Obsolescence';
import ReportsGallery from './ReportsGallery';
import DataFlowDiagram from './DataFlowDiagram';
import NamingDictionary from './NamingDictionary';
import TargetArchitecture from './TargetArchitecture';
import MeetingIntelligence from './MeetingIntelligence';
import Roadmap from './Roadmap';
import AiEnrichment from './AiEnrichment';
import RoadmapView from './RoadmapView';
import MindMap from './MindMap';
import ListsAdmin from './ListsAdmin';
import CustomerAdmin from './CustomerAdmin';
import BulkTools from './BulkTools';
import { ThemeModeToggle } from './ThemeProvider';
import UserAdmin from './UserAdmin';
import PrimordiaLogo from './PrimordiaLogo';
import Login from './Login';
import store, { initStore } from './store';
import { loadConfig, getConfig } from './config';
import { getSession, signOut } from './auth';
import ChangePassword from './ChangePassword';
import AdminPortal from './AdminPortal';

// Self-contained CMDB app. Internal state-based navigation keeps it decoupled
// from the legacy app's router/auth so it renders anywhere we mount it.
const REPORT_VIEWS = ['gallery', 'landscape', 'matrix', 'roadmap', 'mindmap', 'relationmap', 'portfolio', 'obsolescence', 'dataflow', 'targetarch', 'readiness', 'interfaces', 'cost', 'views', 'snapshots', 'benchmarks', 'waveplanner'];
const MANAGE_VIEWS = ['admin', 'users', 'lists', 'catalog', 'catalogintel', 'customers', 'surveys', 'files', 'forms', 'ai', 'extract', 'infra',
  'relwizard', 'csg', 'servermap', 'naming', 'meetings', 'rightsizing', 'templates'];

export default function CmdbApp() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('optimum');
  const [view, setView] = useState('inventory'); // inventory | dashboard | factsheet
  const [selectedId, setSelectedId] = useState(null);
  const [booting, setBooting] = useState(true);
  const [session, setSession] = useState(null);
  const [reportsAnchor, setReportsAnchor] = useState(null);
  const [manageAnchor, setManageAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [loadingTenant, setLoadingTenant] = useState(false);
  const theme = useTheme();
  // Below md the toolbar cannot hold the menus, the tenant switcher and the
  // account block at once, so navigation moves into a drawer.
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  // Boot: load runtime config -> require auth when deployed -> hydrate the store.
  useEffect(() => {
    (async () => {
      await loadConfig();
      const s = getSession();
      setSession(s);
      if (!getConfig().authRequired || s) {
        // A master lands in 'optimum', which holds no records, so load a
        // workspace that does — otherwise the first screen is empty and reads
        // as a broken app rather than as an empty master view.
        const landing = (s && s.tenantId && !(s.groups || []).includes('OptimumAdmins'))
          ? s.tenantId : 'demo';
        await initStore({ tenantId: landing });
        const all = await store.listTenants();
        // Only a master may see the client roster. For a client user the switcher
        // must contain exactly their own tenant — otherwise the list of Optimum's
        // clients leaks, and on the offline fallback path they could switch into one.
        const isMaster = !!s && (s.groups || []).includes('OptimumAdmins');
        setTenants(isMaster || !getConfig().authRequired ? all : all.filter((t) => t.id === s.tenantId));
        // A client user only ever lands in their own portal.
        if (s && s.tenantId && !(s.groups || []).includes('OptimumAdmins')) setTenantId(s.tenantId);
      }
      setBooting(false);
    })();
  }, []);

  const onSignedIn = async (s) => {
    setSession(s);
    setBooting(true);
    const landing2 = (s.tenantId && !(s.groups || []).includes('OptimumAdmins')) ? s.tenantId : 'demo';
    await initStore({ tenantId: landing2 });
    const all2 = await store.listTenants();
    const master2 = (s.groups || []).includes('OptimumAdmins');
    setTenants(master2 ? all2 : all2.filter((t) => t.id === s.tenantId));
    if (s.tenantId && !(s.groups || []).includes('OptimumAdmins')) setTenantId(s.tenantId);
    setBooting(false);
  };

  // `loadingTenant` covers a workspace SWITCH, which fetches. Without it the
  // previous workspace's records stay on screen under the new workspace's name
  // — a master would read one client's estate as another's.
  if (booting || loadingTenant) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f6f8' }}>
        <Stack alignItems="center" spacing={2}>
          <PrimordiaLogo size={64} spinning />
          <Typography sx={{ fontSize: 11, color: 'text.secondary', letterSpacing: 1.5 }}>
            {loadingTenant ? 'LOADING WORKSPACE' : 'LOADING'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Gate: when deployed, nothing renders without a valid Cognito session.
  if (getConfig().authRequired && !session) {
    return <Login onSignedIn={onSignedIn} />;
  }

  // Each workspace is fetched on demand. Nothing is preloaded, because loading
  // every client at once is what exceeded the response limit and silently
  // dropped the app onto seed data.
  const switchTenant = async (id) => {
    setTenantId(id);
    if (id === 'optimum') return;          // master workspace holds no records
    setLoadingTenant(true);
    await initStore({ tenantId: id, seedIfEmpty: false });
    setLoadingTenant(false);
  };

  const open = (id) => { setSelectedId(id); setView('factsheet'); };
  const current = tenants.find((t) => t.id === tenantId);

  // Writing from the master workspace used to silently redirect into the 'nghs'
  // CLIENT workspace — a development shortcut from when nghs was the only tenant
  // with data. With several real clients loaded, and Optimum staff working from
  // master by default, that means a new record lands in a real client's estate
  // with no indication and no undo. Refuse instead: master is a place to look
  // across clients, not a place to author into one.
  const canAuthorHere = tenantId !== 'optimum';

  const createNew = async () => {
    if (!canAuthorHere) return;
    const fs = await store.createFactSheet(tenantId, 'Application', 'New Application');
    open(fs.id);
  };

  const reset = async () => { await store.resetToSeed(); setView('inventory'); setSelectedId(null); };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={1} sx={{ borderBottom: '3px solid #0D5B9A' }}>
        <Toolbar sx={{ gap: 2 }}>
          {/* Optimum brand wordmark — infinity mark + navy/blue, grey descriptor */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <PrimordiaLogo size={36} />
            <Box sx={{ lineHeight: 1 }}>
              <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: 20, lineHeight: 1, letterSpacing: 0.3 }}>
                Primordi<span style={{ color: '#0D5B9A' }}>a</span>
              </Typography>
              <Typography sx={{ fontSize: 8, color: '#5C6B7A', letterSpacing: 1.4, fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>
                WHERE ENTERPRISE SYSTEMS BEGIN
              </Typography>
              <Typography sx={{ fontSize: 7.5, color: '#9AA6B2', letterSpacing: 0.5, fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                by Optimum Healthcare IT
              </Typography>
            </Box>
          </Box>

          {/* White-labelling: the client's own logo in their portal */}
          {current?.logo && current.kind === 'client' && (
            <>
              <Box sx={{ width: '1px', height: 34, bgcolor: 'divider', mx: 1.5, display: { xs: 'none', sm: 'block' } }} />
              <Box component="img" src={current.logo} alt={current.name}
                sx={{ height: 30, maxWidth: { sm: 130, lg: 210 }, objectFit: 'contain', display: { xs: 'none', sm: 'block' } }} />
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {compact && (
            <IconButton onClick={() => setNavOpen(true)} aria-label="Open navigation" sx={{ color: 'inherit' }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: compact ? 'none' : 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ListAltIcon />} onClick={() => setView('inventory')}
            variant={view === 'inventory' ? 'contained' : 'text'}>Inventory</Button>
          <Button startIcon={<DashboardIcon />} onClick={() => setView('dashboard')}
            variant={view === 'dashboard' ? 'contained' : 'text'}>Dashboards</Button>

          {/* Grouped menus — a flat button per view stopped scaling once the
              reporting and admin screens landed. */}
          <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setReportsAnchor(e.currentTarget)}
            variant={REPORT_VIEWS.includes(view) ? 'contained' : 'text'}>Reports</Button>
          <Menu anchorEl={reportsAnchor} open={!!reportsAnchor} onClose={() => setReportsAnchor(null)}>
            {[['gallery', 'All reports (gallery)'], ['landscape', 'Landscape heat map'], ['matrix', 'Matrix & circle map'],
              ['roadmap', 'Migration roadmap'], ['mindmap', 'Relationship mind map'],
              ['relationmap', 'Relation map (printable)'],
              ['portfolio', 'Portfolio & radar'],
              ['obsolescence', 'Obsolescence & technology risk'],
              ['dataflow', 'Data flow diagram'],
              ['targetarch', 'Target architecture (Epic CSG)'],
              ['readiness', 'Cutover readiness & gaps'], ['interfaces', 'Interface engines'],
              ['cost', 'Cost modelling'], ['views', 'Saved views & reports'], ['snapshots', 'Progress since kickoff'], ['benchmarks', 'Cross-client benchmarks'], ['waveplanner', 'Wave planner (dependency-ordered)']].map(([k, label]) => (
              <MenuItem key={k} selected={view === k}
                onClick={() => { setView(k); setReportsAnchor(null); }}>{label}</MenuItem>
            ))}
          </Menu>

          <Button size="small" onClick={() => setView('whatsnew')}
            variant={view === 'whatsnew' ? 'contained' : 'text'}
            sx={{ textTransform: 'none', fontSize: 12 }}>What&rsquo;s coming</Button>

          <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setManageAnchor(e.currentTarget)}
            variant={MANAGE_VIEWS.includes(view) ? 'contained' : 'text'}>Manage</Button>
          <Menu anchorEl={manageAnchor} open={!!manageAnchor} onClose={() => setManageAnchor(null)}>
            {/* One entry, because users, keys, values and clients are one job:
                deciding what people may do. They were four unrelated lines in a
                menu of twenty. */}
            <MenuItem selected={view === 'admin'} onClick={() => { setView('admin'); setManageAnchor(null); }}>
              Administration
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setBulkOpen(true); setManageAnchor(null); }}>Bulk edit &amp; duplicates</MenuItem>
            <MenuItem selected={view === 'surveys'} onClick={() => { setView('surveys'); setManageAnchor(null); }}>Discovery surveys</MenuItem>
            <MenuItem selected={view === 'infra'} onClick={() => { setView('infra'); setManageAnchor(null); }}>Infrastructure &amp; server mapping</MenuItem>
            <MenuItem selected={view === 'extract'} onClick={() => { setView('extract'); setManageAnchor(null); }}>Extract from document</MenuItem>
            <MenuItem selected={view === 'relwizard'} onClick={() => { setView('relwizard'); setManageAnchor(null); }}>Relationship wizard</MenuItem>
            <MenuItem selected={view === 'csg'} onClick={() => { setView('csg'); setManageAnchor(null); }}>CSG target state (Epic)</MenuItem>
            <MenuItem selected={view === 'meetings'} onClick={() => { setView('meetings'); setManageAnchor(null); }}>Meeting transcripts</MenuItem>
            <MenuItem selected={view === 'naming'} onClick={() => { setView('naming'); setManageAnchor(null); }}>Naming dictionary</MenuItem>
            <MenuItem selected={view === 'servermap'} onClick={() => { setView('servermap'); setManageAnchor(null); }}>Server &rarr; deployment mapping</MenuItem>
            <MenuItem selected={view === 'rightsizing'} onClick={() => { setView('rightsizing'); setManageAnchor(null); }}>Rightsizing</MenuItem>
            <MenuItem selected={view === 'templates'} onClick={() => { setView('templates'); setManageAnchor(null); }}>Template library</MenuItem>
            <MenuItem selected={view === 'ai'} onClick={() => { setView('ai'); setManageAnchor(null); }}>AI enrichment</MenuItem>
            <MenuItem selected={view === 'forms'} onClick={() => { setView('forms'); setManageAnchor(null); }}>Discovery forms (Excel)</MenuItem>
            <MenuItem selected={view === 'files'} onClick={() => { setView('files'); setManageAnchor(null); }}>Documents</MenuItem>
            {current?.kind === 'master' && [
              <Divider key="d" />,
              <MenuItem key="cat" selected={view === 'catalog'} onClick={() => { setView('catalog'); setManageAnchor(null); }}>Master catalog</MenuItem>,
              <MenuItem key="catintel" selected={view === 'catalogintel'} onClick={() => { setView('catalogintel'); setManageAnchor(null); }}>Cross-client app intelligence</MenuItem>,
            ]}
          </Menu>
          </Box>
          <ThemeModeToggle />

          {/* Tenant / client portal switcher */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: { xs: 0, md: 2 } }}>
            <Select size="small" value={tenantId} onChange={(e) => { switchTenant(e.target.value); setView('inventory'); }}
              sx={{ minWidth: { xs: 140, md: 240 }, bgcolor: 'action.hover' }}
              renderValue={(id) => {
                const t = tenants.find((x) => x.id === id);
                return (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {t?.logo ? (
                      <img src={t.logo} alt="" style={{ height: 18, maxWidth: 60, objectFit: 'contain' }} />
                    ) : (
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t?.color }} />
                    )}
                    <span>{t?.name}</span>
                    {t?.kind === 'master' && <Chip size="small" label="MASTER" sx={{ height: 16, fontSize: 9, bgcolor: '#021F4B', color: '#fff' }} />}
                    {t?.status === 'archived' && <Chip size="small" label="ARCHIVED" sx={{ height: 16, fontSize: 9, bgcolor: '#9E9E9E', color: '#fff' }} />}
                  </Stack>
                );
              }}
            >
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color }} />
                    <span>{t.name}</span>
                    {t.kind === 'master' && <Chip size="small" label="MASTER" sx={{ height: 16, fontSize: 9 }} />}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </Stack>
          {session ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1 }}>
              <Box sx={{ textAlign: 'right', lineHeight: 1.1, display: { xs: 'none', md: 'block' } }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#021F4B' }}>{session.email}</Typography>
                <Typography sx={{ fontSize: 9, color: '#5C6B7A' }}>
                  {(session.groups || []).includes('OptimumAdmins') ? 'Optimum admin' : (session.tenantName || session.tenantId || 'client user')}
                </Typography>
              </Box>
              <Button size="small" startIcon={<LogoutIcon />} sx={{ color: '#5C6B7A' }}
                onClick={(e) => setAccountAnchor(e.currentTarget)}>
                Account
              </Button>
              <Menu anchorEl={accountAnchor} open={!!accountAnchor} onClose={() => setAccountAnchor(null)}>
                <MenuItem onClick={() => { setChangePwOpen(true); setAccountAnchor(null); }}>
                  Change password
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setAccountAnchor(null); signOut(); setSession(null); }}>
                  Sign out
                </MenuItem>
              </Menu>
            </Stack>
          ) : (
            <Button size="small" startIcon={<RestartAltIcon />} onClick={reset} sx={{ color: '#999' }}>Reset</Button>
          )}
        </Toolbar>
      </AppBar>

      {current?.kind === 'master' && (
        <Box sx={{ bgcolor: '#021F4B', color: '#fff', px: 3, py: 0.5, fontSize: 13 }}>
          Master workspace — viewing fact sheets across <b>all client portals</b>. Switch the selector to enter a single client's portal.
        </Box>
      )}

      <Drawer anchor="right" open={navOpen} onClose={() => setNavOpen(false)}>
        <Box sx={{ width: 260 }} role="navigation">
          <List dense>
            <ListSubheader>Explore</ListSubheader>
            {/* whatsnew is in neither view registry, so it has to be listed
                explicitly here — below md the toolbar is hidden and this drawer
                is the only way to reach anything. */}
            {[['inventory', 'Inventory'], ['dashboard', 'Dashboards'], ['whatsnew', "What's coming"]].map(([k, label]) => (
              <ListItemButton key={k} selected={view === k} onClick={() => { setView(k); setNavOpen(false); }}>
                <ListItemText primary={label} />
              </ListItemButton>
            ))}
            <ListSubheader>Reports</ListSubheader>
            {[['gallery', 'All reports (gallery)'], ['landscape', 'Landscape heat map'], ['matrix', 'Matrix & circle map'],
              ['roadmap', 'Migration roadmap'], ['mindmap', 'Relationship mind map'],
              ['relationmap', 'Relation map (printable)'],
              ['portfolio', 'Portfolio & radar'],
              ['obsolescence', 'Obsolescence & technology risk'],
              ['dataflow', 'Data flow diagram'],
              ['targetarch', 'Target architecture (Epic CSG)'],
              ['readiness', 'Cutover readiness & gaps'], ['interfaces', 'Interface engines'],
              ['cost', 'Cost modelling'], ['views', 'Saved views & reports'], ['snapshots', 'Progress since kickoff'], ['benchmarks', 'Cross-client benchmarks'], ['waveplanner', 'Wave planner (dependency-ordered)']].map(([k, label]) => (
              <ListItemButton key={k} selected={view === k} onClick={() => { setView(k); setNavOpen(false); }}>
                <ListItemText primary={label} />
              </ListItemButton>
            ))}
            <ListSubheader>Manage</ListSubheader>
            {[['admin', 'Administration'], ['surveys', 'Discovery surveys'], ['forms', 'Discovery forms (Excel)'], ['files', 'Documents']].map(([k, label]) => (
              <ListItemButton key={k} selected={view === k} onClick={() => { setView(k); setNavOpen(false); }}>
                <ListItemText primary={label} />
              </ListItemButton>
            ))}
            <ListItemButton onClick={() => { setBulkOpen(true); setNavOpen(false); }}>
              <ListItemText primary="Bulk edit & duplicates" />
            </ListItemButton>
            {current?.kind === 'master' && [
              <ListItemButton key="cat" selected={view === 'catalog'} onClick={() => { setView('catalog'); setNavOpen(false); }}>
                <ListItemText primary="Master catalog" />
              </ListItemButton>,
              <ListItemButton key="cust" selected={view === 'customers'} onClick={() => { setView('customers'); setNavOpen(false); }}>
                <ListItemText primary="Customers" />
              </ListItemButton>,
            ]}
          </List>
        </Box>
      </Drawer>

      {view === 'inventory' && <Inventory tenantId={tenantId} onOpen={open} onCreate={createNew} />}
      {view === 'dashboard' && <Dashboard tenantId={tenantId} />}
      {view === 'landscape' && <LandscapeReport tenantId={tenantId} onOpen={open} />}
      {view === 'catalog' && current?.kind === 'master' && <MasterCatalog onOpenInstance={open} />}
      {view === 'admin' && (
        <AdminPortal tenants={tenants} tenantId={tenantId}
          onTenantsChanged={() => store.listTenants().then(setTenants)} />
      )}
      {/* The old direct routes stay reachable — deep links and anything that
          still sets these views keep working, they are just no longer offered
          in the menu. */}
      {view === 'users' && <UserAdmin tenants={tenants} />}
      {view === 'matrix' && <MatrixReport tenantId={tenantId} onOpen={open} />}
      {view === 'roadmap' && <RoadmapView tenantId={tenantId} onOpen={open} />}
      {view === 'mindmap' && <MindMap tenantId={tenantId} onOpen={open} />}
      {view === 'lists' && <ListsAdmin tenantId={tenantId} />}
      {view === 'readiness' && <ReadinessView tenantId={tenantId} onOpen={open} />}
      {view === 'interfaces' && <InterfaceHub tenantId={tenantId} onOpen={open} />}
      {view === 'cost' && <CostModel tenantId={tenantId} />}
      {view === 'surveys' && <SurveyWorkflow tenantId={tenantId} />}
      {view === 'files' && <FileExchange tenantId={tenantId} />}
      {view === 'forms' && <FormsRoundtrip tenantId={tenantId} />}
      {view === 'views' && <SavedViews tenantId={tenantId} onOpen={open} />}
      {view === 'snapshots' && <SnapshotCompare tenantId={tenantId} onOpen={open} />}
      {view === 'benchmarks' && <Benchmarks tenantId={tenantId} />}
      {view === 'waveplanner' && <WavePlanner tenantId={tenantId} onOpen={open} />}
      {view === 'extract' && <DocumentExtract tenantId={tenantId} />}
      {view === 'infra' && <InfrastructureImport tenantId={tenantId} />}
      {view === 'relwizard' && <RelationshipWizard tenantId={tenantId} onOpen={open} />}
      {view === 'csg' && <CsgTargetState tenantId={tenantId} />}
      {view === 'servermap' && <ServerMapping tenantId={tenantId} onOpen={open} />}
      {view === 'rightsizing' && <Rightsizing tenantId={tenantId} />}
      {view === 'templates' && <TemplateLibrary tenantId={tenantId} />}
      {view === 'relationmap' && <RelationMap tenantId={tenantId} onOpen={open} />}
      {view === 'portfolio' && <PortfolioReport tenantId={tenantId} onOpen={open} />}
      {view === 'obsolescence' && <Obsolescence tenantId={tenantId} />}
      {view === 'dataflow' && <DataFlowDiagram tenantId={tenantId} onOpen={open} />}
      {view === 'naming' && <NamingDictionary tenantId={tenantId} />}
      {view === 'meetings' && <MeetingIntelligence tenantId={tenantId} onOpen={open} />}
      {view === 'targetarch' && <TargetArchitecture tenantId={tenantId} />}
      {/* Visible to every signed-in user, client or Optimum — the content is
          written to be safe for anyone with a login to read. */}
      {view === 'whatsnew' && <Roadmap />}
      {/* The gallery launches a report by view key. Saved configuration is
          carried but most reports do not yet accept one, so they open at their
          own default — the gallery says so rather than implying it applied. */}
      {view === 'gallery' && <ReportsGallery tenantId={tenantId} onOpenView={(k) => k && setView(k)} />}
      {/* Master-only: shows NAMED client data, unlike the anonymised Benchmarks
          screen. The component gates itself too — this is defence in depth. */}
      {view === 'catalogintel' && current?.kind === 'master' && <CatalogIntelligence tenantId={tenantId} />}
      {view === 'ai' && <AiEnrichment tenantId={tenantId} />}
      {view === 'customers' && current?.kind === 'master' && <CustomerAdmin onChanged={() => store.listTenants().then(setTenants)} />}
      {/* Bulk edit is a WRITE tool, so it must not open against master and
          redirect into a client. Only mount it in a workspace we may author in. */}
      {canAuthorHere && (
        <BulkTools open={bulkOpen} onClose={() => setBulkOpen(false)} tenantId={tenantId} onChanged={() => setView('inventory')} />
      )}
      {/* Not gated on canAuthorHere: changing your own password is not authoring
          in this workspace, and a viewer — or anyone sitting in the master
          workspace — must still be able to do it. */}
      <ChangePassword open={changePwOpen} onClose={() => setChangePwOpen(false)} />
      {view === 'factsheet' && selectedId && (
        <FactSheetDetail id={selectedId} tenantId={tenantId} onBack={() => setView('inventory')} onOpen={open} />
      )}
    </Box>
  );
}
